import React, { useState } from 'react';
import './DocumentGrid.css'; // External CSS for styling
import PdfIcon from '../../assets/images/PdfIcon.svg';
import ImageIcon from '../../assets/images/ImageIcon.svg';
import { ImageViewModal } from 'components/shared';
import { strings } from 'i18n/Strings';
import { safeDateFormat } from 'utils/GeneralUtil';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Grid } from '@mui/material';
import { getResource } from 'service/UserService';

// Define the TypeScript type for the document structure
interface Document {
  documentImageId: number;
  userId: number;
  fileName: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
  type: string;
  image: string;
  isDeleted?: boolean;
}

// Props type for the component
interface DocumentGridProps {
  documents: Document[];
  hideTitle?: boolean;
}

interface HeaderProps {
  label: string;
  sortKey: string;
  currentSortBy: string;
  currentSortOrder: string;
  setSortBy: (key: string) => void;
  setSortOrder: (order: 'ASC' | 'DESC') => void;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const SortableHeader = ({
  label,
  sortKey,
  currentSortBy,
  currentSortOrder,
  setSortBy,
  setSortOrder,
}: HeaderProps) => {
  const isCurrentSortKey = currentSortBy === sortKey;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSortClick = () => {
    const newOrder =
      isCurrentSortKey && currentSortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(sortKey);
    setSortOrder(newOrder);
  };

  return (
    <h5>
      {label}{' '}
      {isCurrentSortKey && currentSortOrder === 'DESC' ? (
        <ExpandMoreIcon className="expand-icon" onClick={handleSortClick} />
      ) : (
        <ExpandLessIcon className="expand-icon" onClick={handleSortClick} />
      )}
    </h5>
  );
};

const DocumentGrid: React.FC<DocumentGridProps> = ({
  documents,
  hideTitle = false,
}) => {
  const [viewImage, setViewImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<string>('DESC');

  const handleDocumentClick = async (doc: Document): void => {
    const res = await getResource(doc.image);
    if (!res?.url) return;
    if (doc?.type === 'application/pdf') {
      const link = document.createElement('a');
      link.href = res.url;
      link.download = `${doc.fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setOpenImageDialog(true);
      setViewImage(res.url);
    }
  };

  const handleImageDialogClose = (): void => {
    setOpenImageDialog(false);
  };

  documents.sort((a: unknown, b: unknown) => {
    if (sortBy === 'fileName') {
      return sortOrder === 'DESC'
        ? b[sortBy].localeCompare(a[sortBy])
        : a[sortBy].localeCompare(b[sortBy]);
    } else {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'DESC' ? dateB - dateA : dateA - dateB;
    }
  });
  return documents.length ? (
    <div>
      {!hideTitle ? <h4>Document List</h4> : null}

      <Grid
        container
        spacing={2}
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        className="grid-container"
      >
        <Grid item size="auto" className="file-container">
          <SortableHeader
            label="Document Name"
            sortKey="fileName"
            currentSortBy={sortBy}
            currentSortOrder={sortOrder}
            setSortBy={setSortBy}
            setSortOrder={setSortOrder}
          />
        </Grid>
        <Grid item size="auto" className="file-container">
          <SortableHeader
            label="Uploaded on"
            sortKey="createdBy"
            currentSortBy={sortBy}
            currentSortOrder={sortOrder}
            setSortBy={setSortBy}
            setSortOrder={setSortOrder}
          />
        </Grid>
      </Grid>
      <div className="grid-container">
        {documents
          .sort((a: unknown, b: unknown) =>
            sortOrder === 'DESC'
              ? b[sortBy] - a[sortBy]
              : a[sortBy] - b[sortBy],
          )
          .map((doc) => (
            <div key={doc.documentImageId} className="grid-item">
              <div
                className="document-info"
                onClick={() => {
                  handleDocumentClick(doc);
                }}
              >
                {/* Conditional rendering based on file type */}
                {doc.type === 'application/pdf' ? (
                  <img src={PdfIcon} alt={doc.fileName} className="icon" />
                ) : (
                  <img src={ImageIcon} alt={doc.fileName} className="icon" />
                )}

                <h4 className="document-name">{doc.fileName}</h4>
                {doc.isDeleted && (
                  <p className="upload-date">Document Deleted By User</p>
                )}
                <p className="upload-date">
                  {safeDateFormat(doc.createdAt)}
                </p>
              </div>
            </div>
          ))}
      </div>
      <ImageViewModal
        open={openImageDialog}
        onClose={handleImageDialogClose}
        viewImageUrl={viewImage}
        title={`${String(strings.displayText.imageView)}`}
      />
    </div>
  ) : null;
};

export default DocumentGrid;
